import {
  HStack,
  Icon,
  Center,
  InputGroup,
  InputLeftElement,
  Input,
  InputRightElement,
  Box,
  IconButton,
  Flex,
  Button,
  Text,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { IoSearch } from 'react-icons/io5'
import { TbAdjustmentsHorizontal } from 'react-icons/tb'
import { colors } from '../../../../ui-kit'
import { MLContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import { CustomMetadataContext } from '../../../providers/CustomMetadataProvider'
import type {
  CustomMetadataResponse,
  SearchQueryJson,
} from '../../../interfaces'
import { getSearchQueryFromJSON } from '../../../../utils/mediaLibrary'
import SearchMenu from './SearchMenu'

interface Props {
  searchFocused: boolean
  setSearchFocused(val: boolean): void
  resetSearch(): void
}

const SearchMediaCollection = ({
  searchFocused,
  setSearchFocused,
  resetSearch,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [state, dispatch] = useContext(MLContext)

  const [nameValue, setNameValue] = useState<string | null>(
    state.nameValue ?? null,
  )

  useEffect(() => {
    setNameValue(state.nameValue ?? null)
  }, [state.nameValue])

  const [searchQueryJson, setSearchQueryJson] = useState<SearchQueryJson>({
    boolean: 'and',
    filterGroups: [],
  })

  useEffect(() => {
    if (!state.searchQuery) {
      // handle reset
      setSearchQueryJson({
        boolean: 'and',
        filterGroups: [],
      })
    }
  }, [state.searchQuery])

  useEffect(() => {
    if (state.searchQueryJson.filterGroups.length > 0) {
      dispatch([
        {
          type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
          payload: true,
        },
      ])
    }
  }, [state.searchQueryJson])
  const customMetadataFields = useContext(CustomMetadataContext).cmFields.map(
    x => ({
      ...x,
      name: `"customMetadata.${x.name}"`,
    }),
  )

  const customMetadataFieldsMapping: {
    [fieldName: string]: CustomMetadataResponse
  } = useMemo(() => {
    const ret: {
      [fieldName: string]: CustomMetadataResponse
    } = {}
    customMetadataFields.forEach(customMetadataField => {
      ret[customMetadataField.name] =
        customMetadataField as CustomMetadataResponse
    })
    return ret
  }, [customMetadataFields])

  const searchQuery = useMemo(() => {
    const searchQueryParams: string[] = []
    if (nameValue) {
      searchQueryParams.push(`(name HAS "${nameValue}")`)
    }
    if (searchQueryJson.filterGroups.length) {
      const filterSearchQuery = getSearchQueryFromJSON({
        searchQueryJson,
        customMetadataFieldsMapping,
      })
      searchQueryParams.push(filterSearchQuery)
    }
    return searchQueryParams.join(' AND ')
  }, [nameValue, searchQueryJson, customMetadataFieldsMapping])

  const clearInputs = (): void => {
    setNameValue(null)
    setSearchQueryJson({
      boolean: 'and',
      filterGroups: [],
    })
  }

  const onSubmitSearch = (): void => {
    dispatch([
      {
        type: MediaLibraryActions.SET_SEARCH_NAME_VALUE,
        payload: nameValue ?? '',
      },
      {
        type: MediaLibraryActions.SET_MEDIA_COLLECTION_SEARCH_QUERY,
        payload: searchQuery,
      },
      {
        type: MediaLibraryActions.SET_MEDIA_COLLECTION_SEARCH_QUERY_JSON,
        payload: searchQueryJson,
      },
    ])
    window.trackEvent({
      action: 'Submit',
      category: 'Media collection',
      label: 'Search',
    })
  }

  const inputRef = useRef<any>()

  const searchInputActualRef = useRef(null)

  const inputBoxLeftElement: React.ReactElement = (
    <Center width="2.5rem">
      <Icon color="#9DA3AE" boxSize="1.25rem" as={IoSearch} />
    </Center>
  )
  const inputBoxRightElement = useMemo<React.ReactElement | null>(
    () => (
      <Flex>
        {(nameValue === '' && searchQueryJson.filterGroups.length === 0) ||
        !state.openAdvancedSearch ||
        searchFocused ? null : (
          <Center width="2.5rem">
            <Button
              cursor="pointer"
              variant="ghost"
              onClick={() => {
                void resetSearch()
                void setSearchFocused(false)
              }}
            >
              <Text color="appBattleshipGrey" fontWeight="normal">
                {t('clear_search', 'clear')}
              </Text>
            </Button>
          </Center>
        )}
        <IconButton
          aria-label="Advanced Search"
          icon={<TbAdjustmentsHorizontal />}
          fontSize="1.5em"
          colorScheme={
            state.openAdvancedSearch ? colors.appBlue : "#9DA3AE"
          }
          onClick={() => {
            dispatch([
              {
                type: MediaLibraryActions.SET_OPEN_ADVANCED_SEARCH,
                payload: true,
              },
            ])
          }}
          variant="outline"
          borderTop="none"
          borderBottom="none"
          borderRight="none"
          borderRadius="0"
          borderColor={colors.appPaleLilac}
        />
      </Flex>
    ),
    [nameValue, searchQueryJson, state.openAdvancedSearch],
  )

  return (
    <>
      <HStack
        ref={inputRef}
        spacing="0"
        cursor="pointer"
        zIndex={1401}
        onClick={() => void setSearchFocused(true)}
        height="35px"
        width="full"
        bgColor={colors.appWhite}
        paddingEnd={10}
      >
        <InputGroup
          ref={searchInputActualRef}
          display={searchFocused ? 'none' : 'flex'}
        >
          <InputLeftElement
            children={inputBoxLeftElement}
            position="static"
            width="unset"
            borderWidth="1px"
            borderColor="#e6e7ea"
            borderRadius="0.25rem"
            borderRightWidth="0"
            borderRightRadius="0"
            paddingLeft="0.5em"
          />
          <Input
            isReadOnly
            cursor="pointer"
            placeholder={t('start_typing_to_search', 'Start typing to search')}
            value={nameValue ?? ''}
            focusBorderColor="none"
            _hover={{ borderColor: '#e6e7ea' }}
            borderLeftRadius="0"
            borderLeftWidth="0"
            position="static"
            paddingLeft="0"
          />
          {inputBoxRightElement ? (
            <InputRightElement width="unset" children={inputBoxRightElement} />
          ) : null}
        </InputGroup>
      </HStack>

      {searchFocused && (
        <>
          <SearchMenu
            searchInputRef={inputRef}
            searchInputActualRef={searchInputActualRef}
            onSubmitSearch={onSubmitSearch}
            setSearchFocused={setSearchFocused}
            inputBoxLeftElement={inputBoxLeftElement}
            inputBoxRightElement={
              !state.openAdvancedSearch ? inputBoxRightElement : null
            }
            nameValue={nameValue}
            setNameValue={setNameValue}
            customMetadataFields={customMetadataFields}
            customMetadataFieldsMapping={customMetadataFieldsMapping}
            onReset={() => {
              clearInputs()
              setSearchFocused(false)
              resetSearch()
            }}
            onClose={() => {
              setSearchFocused(false)
            }}
            searchQueryJson={searchQueryJson}
            setSearchQueryJson={setSearchQueryJson}
          />
          {ReactDOM.createPortal(
            <Box
              className="dimmer"
              position="fixed"
              h="100vh"
              w="100vw"
              top="0"
              left="0"
              bgColor="blackAlpha.600"
              zIndex="overlay"
            />,
            document.body,
          )}
        </>
      )}
    </>
  )
}

export default SearchMediaCollection
